import { useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';

let socket = null;

// Per-room unread counts: { roomId: count }
let unreadByRoom = {};
let unreadListeners = new Set();

// Get total unread across all rooms
function getTotalUnread() {
  return Object.values(unreadByRoom).reduce((sum, count) => sum + count, 0);
}

// Notify all listeners
function notifyListeners() {
  const total = getTotalUnread();
  unreadListeners.forEach(listener => listener({ total, byRoom: { ...unreadByRoom } }));
}

// Increment unread for a specific room
export function incrementRoomUnread(roomId) {
  unreadByRoom[roomId] = (unreadByRoom[roomId] || 0) + 1;
  notifyListeners();
  // Play notification sound
  if (typeof window !== 'undefined') {
    const audio = new Audio('/notification.wav');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  }
}

// Mark a room as read (clear its unread count)
export function markRoomAsRead(roomId) {
  if (unreadByRoom[roomId]) {
    delete unreadByRoom[roomId];
    notifyListeners();
  }
}

// Set initial unread counts (from API on load)
export function setInitialUnread(counts) {
  unreadByRoom = { ...counts };
  notifyListeners();
}

// Reset all unread
export function resetAllUnread() {
  unreadByRoom = {};
  notifyListeners();
}

// Hook to subscribe to unread counts
export function useUnreadCounts() {
  const [state, setState] = useState({ total: getTotalUnread(), byRoom: { ...unreadByRoom } });
  
  useEffect(() => {
    const listener = (newState) => setState(newState);
    unreadListeners.add(listener);
    // Set initial state
    setState({ total: getTotalUnread(), byRoom: { ...unreadByRoom } });
    return () => unreadListeners.delete(listener);
  }, []);
  
  return state;
}

export function useSocket() {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    
    if (!token) {
      return;
    }

    // Reuse existing socket if connected
    if (socket?.connected) {
      setConnected(true);
      return;
    }

    // Create new socket connection
    socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => {
      console.log('🔌 Socket connected');
      setConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('🔌 Socket connection error:', err.message);
      setConnected(false);
    });

    return () => {
      // Don't disconnect on unmount - keep socket alive across navigation
    };
  }, []);

  const joinRoom = useCallback((roomId) => {
    if (socket?.connected) {
      socket.emit('join-room', roomId);
    }
  }, []);

  const leaveRoom = useCallback((roomId) => {
    if (socket?.connected) {
      socket.emit('leave-room', roomId);
    }
  }, []);

  const startTyping = useCallback((roomId) => {
    if (socket?.connected) {
      socket.emit('typing-start', roomId);
    }
  }, []);

  const stopTyping = useCallback((roomId) => {
    if (socket?.connected) {
      socket.emit('typing-stop', roomId);
    }
  }, []);

  const onNewMessage = useCallback((callback) => {
    if (socket) {
      socket.on('new-message', callback);
      return () => socket.off('new-message', callback);
    }
    return () => {};
  }, []);

  const onReactionUpdated = useCallback((callback) => {
    if (socket) {
      socket.on('reaction-updated', callback);
      return () => socket.off('reaction-updated', callback);
    }
    return () => {};
  }, []);

  const onUserTyping = useCallback((callback) => {
    if (socket) {
      socket.on('user-typing', callback);
      return () => socket.off('user-typing', callback);
    }
    return () => {};
  }, []);

  const onUserStoppedTyping = useCallback((callback) => {
    if (socket) {
      socket.on('user-stopped-typing', callback);
      return () => socket.off('user-stopped-typing', callback);
    }
    return () => {};
  }, []);

  const onRoomUpdated = useCallback((callback) => {
    if (socket) {
      socket.on('room-updated', callback);
      return () => socket.off('room-updated', callback);
    }
    return () => {};
  }, []);

  const onRoomCreated = useCallback((callback) => {
    if (socket) {
      socket.on('room-created', callback);
      return () => socket.off('room-created', callback);
    }
    return () => {};
  }, []);

  return {
    socket,
    connected,
    joinRoom,
    leaveRoom,
    startTyping,
    stopTyping,
    onNewMessage,
    onReactionUpdated,
    onUserTyping,
    onUserStoppedTyping,
    onRoomUpdated,
    onRoomCreated,
  };
}

// Disconnect socket on logout
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  unreadByRoom = {};
}
